'use client';

import Image from 'next/image';
import { useLocale, useTranslations } from 'next-intl';
import React from 'react';

import Button from '@/components/button/Button';
import { ButtonStyle } from '@/components/button/ButtonStyle.enum';

import { HrefOrHrefWithParams } from 'next-intl/dist/types/navigation/shared/utils';

import { getAgeRatingIcon } from '@/helpers/movie-helper';
import { getShowingInfo } from '@/helpers/showing-helper';
import { useAgeRatingText } from '@cineamo/ui/src/helpers/movie-helper';
import { useIsMobile } from '@cineamo/ui/src/helpers/screen-type';

import { CachedContentData } from '@/hooks/useCachedContentData';

import { IconRightArrow16 } from '@cineamo/ui/src/icons/ic-rightArrow';
import DotSeparatedLabel from '@cineamo/ui/src/label/DotSeparatedLabel';

import { Link } from '@/i18n/routing';
import { useDomainConfiguration } from '@/providers/DomainConfigurationProvider';

type SelectedMovieViewProps = Readonly<{
    className: string;
    content: CachedContentData;
    children?: React.ReactNode;
}>;

function SelectedMovieView(props: SelectedMovieViewProps): React.JSX.Element {
    const domainConfiguration = useDomainConfiguration();
    const { className, content, children } = props;

    const t = useTranslations();
    const locale = useLocale();
    const isMobile = useIsMobile('md');
    const countryCode = domainConfiguration.cinema.countryCode;

    const movieTitle = content?.title;
    const movieDescription = content?.description;
    const ageRating = content?.ageRating;
    const ageRatingElement = getAgeRatingIcon(ageRating, countryCode, {
        movieTitle: content?.title,
        movieId: content?.movieId
    });
    const ageRatingText = useAgeRatingText(ageRating, countryCode);
    const subtitleInfo = getShowingInfo({
        content,
        countryCode: countryCode,
        locale,
        t: t,
        showGenres: true,
        ageRatingText: !ageRatingElement ? ageRatingText : undefined
    });

    const programHref = {
        pathname: '/program/[contentId]',
        params: { contentId: content.id }
    } as HrefOrHrefWithParams<string>;

    return (
        <div className={className}>
            {/* Inner wrapper owns the mobile centering + the stretched-link overlay, so the parent-controlled
                positioning/display on the root (e.g. `absolute` for the crossfade) stays untouched. */}
            <div className="relative flex flex-col w-full items-center text-center md:items-start md:text-left">
                {content?.logoImage ? (
                    <div className="relative mb-10 w-[220px] lg:w-[300px] max-w-full aspect-[2/1] [filter:drop-shadow(0px_4px_8px_rgba(0,_0,_0,_0.2))]">
                        <Image
                            src={content.logoImage}
                            alt={movieTitle}
                            fill
                            sizes="(min-width: 768px) 300px, 220px"
                            className="object-contain object-bottom"
                        />
                    </div>
                ) : (
                    <div className="text-headline-20 md:text-headline-24 text-headline-24-custom-style">
                        {movieTitle}
                    </div>
                )}
                <div className="text-body-bold-12 md:text-body-bold-14 mt-6 items-center space-x-10 flex">
                    {/* Kept above the mobile stretched-link overlay so the age rating stays independently clickable */}
                    {ageRatingElement && <span className="relative z-[2]">{ageRatingElement}</span>}
                    <DotSeparatedLabel
                        labels={subtitleInfo}
                        style={{
                            color: domainConfiguration.style.onBackgroundColor,
                            opacity: 0.5
                        }}
                    />
                </div>
                {children ?? (
                    <>
                        {!isMobile && (
                            <div className="text-body-14 mt-16 mb-12 line-clamp-3 md:line-clamp-5">
                                {movieDescription}
                            </div>
                        )}
                        {/* Desktop: explicit "go to screenings" CTA */}
                        <Link
                            className="hidden md:inline-flex text-body-bold-12 md:text-body-bold-14 text-cyan clickable-element mt-6"
                            style={{
                                color: domainConfiguration.style.primaryColor
                            }}
                            href={programHref}
                            aria-label={movieTitle}
                            prefetch={false}>
                            <Button
                                labelClassName="text-body-bold-12 lg:text-body-bold-14"
                                style={ButtonStyle.text_only_no_padding}
                                css={{
                                    color: domainConfiguration.style.primaryColor
                                }}
                                trailingIcon={<IconRightArrow16 />}>
                                {t('home.go-to-screenings')}
                            </Button>
                        </Link>
                        {/* Mobile: the whole view is the link (CTA hidden). z-[1] keeps it below the age rating (z-[2]). */}
                        <Link
                            className="md:hidden absolute inset-0 z-[1]"
                            href={programHref}
                            aria-label={movieTitle}
                            prefetch={false}>
                            <span className="sr-only">{t('home.go-to-screenings')}</span>
                        </Link>
                    </>
                )}
            </div>
        </div>
    );
}

export default SelectedMovieView;
